import React, { useMemo, useState } from "react";
import { useDefaultLayout, usePanelRef } from "react-resizable-panels";
import { Account } from "@common/types/domains/accounting/account";
import { Entity } from "@common/types/entity";
import { fullDateLongFormat } from "@common/helpers/date";
import { useClasses } from "@api/class.api";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import { useLockDate } from "@api/lock.api";
import { JournalEntryScopeSelect } from "@components/accounting/journalEntryDrawer/JournalEntryScopeSelect";
import { JournalEntryScope } from "@components/accounting/journalEntryDrawer/journalEntryScope";
import { JournalEntryConsolidationContext } from "@components/accounting/journalEntryDrawer/journalEntryConsolidationContext";
import { SpreadsheetColumnSettings } from "@components/spreadsheet/SpreadsheetColumnSettings";
import { useSpreadsheetColumnVisibility } from "@components/spreadsheet/useSpreadsheetColumnVisibility";
import { Button } from "@design-system/Button/Button";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { HStack } from "@design-system/HStack/HStack";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { LeftNavClosedIcon, LeftNavOpenedIcon } from "@icons";
import jeStyles from "@components/accounting/journalEntryDrawer/JournalEntryDrawer.module.scss";
import {
    appendEmptyLines,
    EliminatingEntryFormValues,
    EliminatingEntryLineForm,
    getFormValidationMessage,
    getFormValidity,
    getMinimumUnlockedDate,
} from "./eliminatingEntryDrawer.lib";
import { buildEliminatingGridColumns } from "./eliminatingEntryGridColumns";
import {
    buildEliminatingGridTemplateColumns,
    DEFAULT_HIDDEN_ELIMINATING_ENTRY_FIELDS,
    ELIMINATING_ENTRY_COLUMNS_STORAGE_KEY,
    getEliminatingEntryColumnSettings,
    getVisibleEliminatingEntryColumns,
} from "./eliminatingEntrySpreadsheetColumns";
import {
    EliminatingEntryFormFooter,
    EliminatingEntrySubmitMode,
} from "./EliminatingEntryFormFooter";
import { EliminatingEntryGrid } from "./EliminatingEntryGrid";
import { EliminatingEntrySidebar } from "./EliminatingEntrySidebar";

interface Props {
    values: EliminatingEntryFormValues;
    entities: Entity[];
    accountsById: Record<string, Account>;
    accountsByEntityId: Record<number, Account[]>;
    debitTotal: number;
    creditTotal: number;
    canSubmit: boolean;
    isPending: boolean;
    isEditing: boolean;
    isLocked: boolean;
    mutationError: string | null;
    pendingSubmitMode: EliminatingEntrySubmitMode | null;
    scope?: JournalEntryScope;
    scopeEntities?: readonly Entity[];
    consolidationContext?: JournalEntryConsolidationContext;
    onScopeChange?: (scope: JournalEntryScope) => void;
    onDateChange: (date: string) => void;
    onReversedAtChange: (reversedAt: string | null) => void;
    onMemoChange: (memo: string) => void;
    onLinesChange: (lines: EliminatingEntryLineForm[]) => void;
    onSubmit: (mode: EliminatingEntrySubmitMode) => void;
    onDelete: () => void;
}

export const EliminatingEntryDrawerForm: React.FC<Props> = ({
    values,
    entities,
    accountsById,
    accountsByEntityId,
    debitTotal,
    creditTotal,
    canSubmit,
    isPending,
    isEditing,
    isLocked,
    mutationError,
    pendingSubmitMode,
    scope,
    scopeEntities,
    consolidationContext,
    onScopeChange,
    onDateChange,
    onReversedAtChange,
    onMemoChange,
    onLinesChange,
    onSubmit,
    onDelete,
}) => {
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: "eliminating-entry-resizable-panel-group",
        storage: localStorage,
    });
    const detailsPanelRef = usePanelRef();
    const [isDetailExpanded, setIsDetailExpanded] = useState(true);
    const parentLockDate = useLockDate(entities[0]?.id);
    const minDate = getMinimumUnlockedDate(parentLockDate);
    const { isActivated: isClassesActivated } = useClasses();
    const counterpartiesMap = useCounterpartyNameMap();
    const { hiddenFields, toggleField, reset } = useSpreadsheetColumnVisibility(
        DEFAULT_HIDDEN_ELIMINATING_ENTRY_FIELDS,
        ELIMINATING_ENTRY_COLUMNS_STORAGE_KEY,
    );
    const visibleColumns = useMemo(
        () =>
            getVisibleEliminatingEntryColumns({
                hiddenFields,
                isClassesActivated,
            }),
        [hiddenFields, isClassesActivated],
    );
    const columns = useMemo(
        () =>
            buildEliminatingGridColumns({
                visibleColumns,
                entities,
                accountsByEntityId,
                counterpartiesMap,
                debitTotal,
                creditTotal,
            }),
        [
            visibleColumns,
            entities,
            accountsByEntityId,
            counterpartiesMap,
            debitTotal,
            creditTotal,
        ],
    );
    const columnSettings = useMemo(
        () => getEliminatingEntryColumnSettings(isClassesActivated),
        [isClassesActivated],
    );
    const validationMessage = getFormValidationMessage(getFormValidity(values));

    return (
        <form
            onSubmit={(event) => event.preventDefault()}
            className={jeStyles.form}
        >
            <div className={jeStyles.content}>
                <ResizablePanelGroup
                    orientation="horizontal"
                    defaultLayout={defaultLayout}
                    onLayoutChanged={onLayoutChanged}
                >
                    <ResizablePanel
                        defaultSize="85%"
                        minSize="70%"
                        maxSize="100%"
                        id="eliminating-entry-left-panel"
                    >
                        <HStack
                            gap={12}
                            align="center"
                            justify="between"
                            padding={12}
                        >
                            <HStack gap={12} align="center">
                                <DatePicker
                                    mode="day"
                                    variant="panel-input"
                                    className={jeStyles.dateInput}
                                    clearable={false}
                                    minDate={minDate}
                                    value={values.date}
                                    displayFormat={fullDateLongFormat}
                                    disabled={isLocked}
                                    onValueChange={(value) =>
                                        onDateChange(value ?? values.date)
                                    }
                                    buttonProps={{
                                        variant: "primary",
                                        size: "lg",
                                    }}
                                />
                                {scope &&
                                    scopeEntities &&
                                    consolidationContext &&
                                    onScopeChange && (
                                        <JournalEntryScopeSelect
                                            entities={scopeEntities}
                                            consolidationContext={
                                                consolidationContext
                                            }
                                            value={scope}
                                            onValueChange={onScopeChange}
                                            disabled={isEditing || isLocked}
                                        />
                                    )}
                            </HStack>
                            <HStack gap={12} align="center">
                                <SpreadsheetColumnSettings
                                    columns={columnSettings}
                                    hiddenFields={hiddenFields}
                                    onToggle={toggleField}
                                    onReset={reset}
                                />
                                <Button
                                    size="sm"
                                    variant="primary"
                                    type="button"
                                    iconOnly
                                    onClick={() =>
                                        detailsPanelRef.current?.isCollapsed()
                                            ? detailsPanelRef.current.expand()
                                            : detailsPanelRef.current?.collapse()
                                    }
                                    aria-label={
                                        isDetailExpanded
                                            ? "Collapse sidebar"
                                            : "Expand sidebar"
                                    }
                                    aria-expanded={isDetailExpanded}
                                >
                                    {isDetailExpanded ? (
                                        <LeftNavOpenedIcon className="icon-color-gray-900 icon-rotate-180" />
                                    ) : (
                                        <LeftNavClosedIcon className="icon-color-gray-900 icon-rotate-180" />
                                    )}
                                </Button>
                            </HStack>
                        </HStack>
                        <EliminatingEntryGrid
                            lines={values.lines}
                            columns={columns}
                            gridTemplateColumns={buildEliminatingGridTemplateColumns(
                                visibleColumns,
                            )}
                            accountsById={accountsById}
                            accountsByEntityId={accountsByEntityId}
                            readonly={isLocked}
                            onLinesChange={onLinesChange}
                        />
                    </ResizablePanel>
                    <ResizableHandle />
                    <ResizablePanel
                        defaultSize="15%"
                        collapsible
                        minSize={300}
                        maxSize="30%"
                        id="eliminating-entry-details-panel"
                        panelRef={detailsPanelRef}
                        onResize={() =>
                            setIsDetailExpanded(
                                !(
                                    detailsPanelRef.current?.isCollapsed() ??
                                    false
                                ),
                            )
                        }
                        className={jeStyles.sidebarPanel}
                    >
                        <EliminatingEntrySidebar
                            date={values.date}
                            reversedAt={values.reversedAt}
                            memo={values.memo}
                            disabled={isLocked}
                            onReversedAtChange={onReversedAtChange}
                            onMemoChange={onMemoChange}
                        />
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>
            <EliminatingEntryFormFooter
                canSubmit={canSubmit}
                isPending={isPending}
                isEditing={isEditing}
                isLocked={isLocked}
                validationMessage={validationMessage}
                mutationError={mutationError}
                pendingSubmitMode={pendingSubmitMode}
                onAddRows={() => onLinesChange(appendEmptyLines(values.lines))}
                onSubmit={(mode) => {
                    if (!isLocked) {
                        onSubmit(mode);
                    }
                }}
                onDelete={() => {
                    if (!isLocked) {
                        onDelete();
                    }
                }}
            />
        </form>
    );
};
